import { useState } from 'react'
import dayjs from 'dayjs'
import { generatePath, useHistory } from 'react-router-dom'
import { Modal, useModals } from 'almanac-ui/Modal'
import { CONTENT_PLAN_PATH } from 'paths'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCampaignOptions } from 'hooks/queries/campaignOptions/useCampaignOptions'
import { collectResults, getTotalCountFromInfiniteQuery } from 'utils/query'
import { CampaignOption } from 'types'
import { CreateCampaignModal } from 'screens/ContentPlan/CreateCampaignModal/CreateCampaignModal'
import { NoCampaignModal } from '../NoCampaignModal/NoCampaignModal'
import { NewPostPicker, NewPostPickerSkeleton } from './NewPostPicker'
import { ComposeStep } from './ComposeStep'
import { NewPostStep } from './Types'

interface NewPostModalProps {
  initialScheduleId?: number
  onSuccess?: (postsCount: number, brandPlanScheduleId: number) => void
}

export const NewPostModal = ({ initialScheduleId, onSuccess }: NewPostModalProps) => {
  const { data: brandPlan } = useCurrentBrandPlan()
  const { openModal, closeModal, closeAllModals } = useModals()
  const history = useHistory()
  const workspaceId = useCurrentWorkspaceId()
  const schedulesQuery = useCampaignOptions(
    {
      brandPlanId: brandPlan?.id ?? 0,
      start_date: dayjs().format('YYYY-MM-DD'),
      order_direction: 'asc',
      items: 20,
    },
    { enabled: !!brandPlan?.id },
  )
  const schedules = collectResults(schedulesQuery, (d) => d.campaign_options)
  const noCampaignsLoading = schedulesQuery.isLoading
  const hasNoCampaigns = schedulesQuery.isFetched && getTotalCountFromInfiniteQuery(schedulesQuery) === 0

  const [step, setStep] = useState<NewPostStep>('Pick')
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | undefined>(initialScheduleId)
  const [initialIsBlank, setInitialIsBlank] = useState(false)

  if (!brandPlan || noCampaignsLoading) {
    return (
      <Modal.Root size="md">
        <NewPostPickerSkeleton />
      </Modal.Root>
    )
  }

  if (hasNoCampaigns) {
    return (
      <NoCampaignModal
        onCancel={() => closeModal()}
        onCreateCampaign={() => {
          closeModal()
          openModal(CreateCampaignModal, {
            brandPlan,
            onSuccess: () => {
              history.push(generatePath(CONTENT_PLAN_PATH, { workspaceId }))
            },
          })
        }}
      />
    )
  }

  const selectedSchedule = schedules.find((s) => s.id === selectedScheduleId) ?? schedules[0]

  const enterCompose = (schedule: CampaignOption | undefined, isBlank: boolean) => {
    if (!schedule) return
    setSelectedScheduleId(schedule.id)
    setInitialIsBlank(isBlank)
    setStep('Compose')
  }

  const handleGenerateAi = (schedule?: CampaignOption) => enterCompose(schedule, false)
  const handleBlankPost = (schedule?: CampaignOption) => enterCompose(schedule, true)

  const handlePostsCreated = (postsCount: number, brandPlanScheduleId: number) => {
    onSuccess?.(postsCount, brandPlanScheduleId)
    closeAllModals()
  }

  return (
    <Modal.Root size="md">
      {step === 'Pick' && (
        <NewPostPicker
          selectedSchedule={selectedSchedule}
          schedules={schedules}
          isLoadingSchedules={schedulesQuery.isLoading}
          onScheduleChange={setSelectedScheduleId}
          onGenerateAi={handleGenerateAi}
          onBlankPost={handleBlankPost}
        />
      )}

      {step === 'Compose' && selectedSchedule && (
        <ComposeStep
          brandPlan={brandPlan}
          initialSchedule={selectedSchedule}
          schedules={schedules}
          isLoadingSchedules={schedulesQuery.isLoading}
          initialIsBlank={initialIsBlank}
          workspaceId={workspaceId}
          onBack={() => setStep('Pick')}
          onPostsCreated={handlePostsCreated}
        />
      )}
    </Modal.Root>
  )
}
